import React, { useCallback, useEffect, useState } from 'react'
import { Box, Button, Card, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { useNavigate } from 'react-router-dom'
import EmptyState from '../../merchants/shared/EmptyState'
import { C, SX, TYPE } from '../../merchants/shared/tokens'
import { errorText, fetchHistory } from '../ptlSetupApi'
import { COUNT_LABEL, ZONE_MODES, actionLabel, fmtDate, num } from '../constants'
import { Banner, Breadcrumb } from '../components/SetupUi'
import PtlPage from '../shell/PtlPage'
import { useSetupTarget } from './targets'

const PAGE = 20

const countsText = (counts = {}) => Object.keys(COUNT_LABEL).filter((k) => counts[k]).map((k) => `${COUNT_LABEL[k]} ${num(counts[k])}`).join(' · ') || '—'

/**
 * Change history of a PTL zone map or TAT, read from the audit log: who changed it, when, how (upload or back to
 * standard), the file and the counts. Read only: no copy of an old map / TAT is kept, so nothing downloads or restores.
 *
 * @param target  key of SETUP_TARGETS
 */
export default function HistoryScreen({ target }) {
  const { t, error } = useSetupTarget(target)
  return t ? <HistoryBody t={t} /> : <Resolving title='History' error={error} />
}

function HistoryBody({ t }) {
  const navigate = useNavigate()
  const [rows, setRows] = useState(null)
  const [more, setMore] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback((page) => {
    setLoading(true)
    setError('')
    const request = t.history ? t.history(page, PAGE) : fetchHistory(t.kind, t.merchantId, page, PAGE)
    request
      .then((list) => { setRows((cur) => (page === 0 ? list : [...(cur || []), ...list])); setMore(list.length === PAGE) })
      .catch((e) => setError(errorText(e, 'Could not load the history')))
      .finally(() => setLoading(false))
  }, [t])
  useEffect(() => { load(0) }, [load])

  const list = rows || []
  const last = list[0]
  const lastUpload = list.find((v) => (v.action || '').toUpperCase() === 'UPLOAD')
  const kpis = [
    { key: 'count', label: 'Changes', value: `${num(list.length)}${more ? '+' : ''}`, sub: more ? 'Older ones load below' : 'Since PTL Merchant Setup' },
    { key: 'last', label: 'Last change', value: last ? fmtDate(last.createdAt).split(',')[0] : 'None', sub: last ? (last.createdBy || actionLabel(last.action)) : 'Never changed here' },
    { key: 'upload', label: 'Last upload', value: lastUpload ? fmtDate(lastUpload.createdAt).split(',')[0] : 'None', sub: lastUpload ? (lastUpload.fileName || lastUpload.createdBy) : 'Nothing uploaded here yet' },
  ]

  return (
    <PtlPage title={`History · ${t.label}`} eyebrow={t.eyebrow}
      description='Every change saved from PTL Merchant Setup, from the audit log: who, when, the file and what changed. Old versions are not kept, so a change is undone by uploading the corrected file.'
      kpis={kpis} kpisLoading={!rows && !error} kpisLabel={`${t.label} history`}
      breadcrumb={<Breadcrumb parent={t.parent.label} onParent={() => navigate(t.parent.to)} current='History' />}>
      {error && <Banner tone='danger' action={<Button size='small' onClick={() => load(0)} sx={{ textTransform: 'none' }}>Retry</Button>}>{error}</Banner>}
      {rows && list.length === 0 ? (
        <EmptyState title='No changes yet' body={`Changes of the ${t.noun} saved from PTL Merchant Setup show here.`} />
      ) : (
        <Card elevation={0} sx={{ ...SX.card, overflowX: 'auto' }}>
          <Table size='small' sx={{ minWidth: 720 }} aria-label={`${t.label} history`}>
            <TableHead>
              <TableRow>
                {['When', 'What', 'File', 'Changes', 'By'].map((h) => (
                  <TableCell key={h} sx={{ ...TYPE.eyebrow, fontSize: 10, color: C.inkFaint, bgcolor: C.surfaceAlt, borderColor: C.line, py: 1.25, whiteSpace: 'nowrap' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!rows && <TableRow><TableCell colSpan={5} sx={{ ...TYPE.rowHelp, textAlign: 'center', py: 3 }}>Loading history…</TableCell></TableRow>}
              {list.map((v, i) => (
                <TableRow key={`${v.createdAt}-${i}`} sx={{ '& td': { borderColor: C.lineSoft, py: 1.1 } }}>
                  <TableCell sx={{ fontSize: 12.5, color: C.inkBody, whiteSpace: 'nowrap' }}>{fmtDate(v.createdAt)}</TableCell>
                  <TableCell>
                    <Typography sx={{ ...TYPE.value, fontSize: 13 }}>{actionLabel(v.action)}{v.courier ? ` · ${v.courier}` : ''}</Typography>
                    {v.mode && <Typography sx={{ ...TYPE.meta, color: C.inkFaint }}>{ZONE_MODES[v.mode]?.label || v.mode}</Typography>}
                    {v.error && <Typography sx={{ ...TYPE.meta, color: C.danger, maxWidth: 280, overflowWrap: 'anywhere' }}>{v.error}</Typography>}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12.5, color: C.inkMuted, minWidth: 160, maxWidth: 240, overflowWrap: 'anywhere' }}>{v.fileName || '—'}</TableCell>
                  <TableCell sx={{ fontSize: 12.5, color: C.inkBody }}>{countsText(v.counts)}</TableCell>
                  <TableCell sx={{ fontSize: 12.5, color: C.inkMuted, whiteSpace: 'nowrap' }}>{v.createdBy || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {more && (
            <Box sx={{ textAlign: 'center', py: 1.25, borderTop: `1px solid ${C.line}` }}>
              <LoadingButton loading={loading} onClick={() => load(Math.ceil(list.length / PAGE))} sx={{ textTransform: 'none', fontWeight: 600 }}>Load older changes</LoadingButton>
            </Box>
          )}
        </Card>
      )}
    </PtlPage>
  )
}

/** Loading / error frame while a merchant target is resolved. */
function Resolving({ title, error }) {
  return (
    <PtlPage title={title}>
      {error ? <Banner tone='danger'>{error}</Banner> : <Typography role='status' sx={{ ...TYPE.rowHelp, py: 4, textAlign: 'center' }}>Loading…</Typography>}
    </PtlPage>
  )
}
